import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PricingPage from "./PricingPage.jsx";
import RecruiterPromptModal from "./RecruiterPromptModal.jsx";
import MentorCarousel from "./MentorCarousel.js";
import HelpSection from "./Faqs.js";
import FAQSection from "./Faq.js";
import { useGlobalContext } from "../pages/AUTH/GlobalContext.js";
import axios from 'axios';
import {
  ArrowRight,
  Briefcase as BriefcaseIcon,
  Users,
  Flame,
  Clock,
  Inbox,
  Wand2,
  Bell,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Target,
  Activity,
  Layers,
} from "lucide-react";
import BlogCarousel from "./BlogCarousel.js";

import JobCard from '../pages/Jobs/JobCard.jsx'; // Import the JobCard component

import JobBoard from "./JobBoard.js";
const SaveButton = ({ jobId, onSaveSuccess }) => {
  const { user } = useGlobalContext();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const saveJob = async () => {
    if (!user) {
      alert("Please login to save jobs");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("jwtToken");

      await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/jobs/save`,
        {
          userId: user._id,
          jobId: jobId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSaved(true);
      onSaveSuccess?.();

      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error("Error saving job:", error);

      if (error.response?.status === 409) {
        alert("Job already saved!");
      } else {
        alert("Failed to save job");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      onClick={(e) => {
        e.stopPropagation(); // Prevent card click when saving
        saveJob();
      }}
      disabled={saving || saved}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
        saved
          ? "bg-green-50 text-green-600 border border-green-200"
          : "bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100"
      } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {saving ? "Saving..." : saved ? "Saved!" : "Save Job"}
    </button>
  );
};
const HomePage = () => {

  const [applications, setApplications] = useState([]);
  const [coldEmailsSent, setColdEmailsSent] = useState(0);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
    const navigate = useNavigate();
      const { setIsLoggedIn, setUser } = useGlobalContext();
    const [showRecruiterModal, setShowRecruiterModal] = useState(false);

    useEffect(() => {
      const fetchJobs = async () => {
        try {
          setLoadingJobs(true);
          const token = localStorage.getItem('jwtToken');
          
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/jobs/random?limit=6&limitFlag=1`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
  
          if (response.data.success) {
            setJobs(response.data.jobs || []);
          }
        } catch (error) {
          console.error('Error fetching jobs:', error);
        } finally {
          setLoadingJobs(false);
        }
      };
  
      fetchJobs();
    }, []);
useEffect(() => {
  const autoLogin = async () => {
    const token = localStorage.getItem("jwtToken"); // Consistent key
    console.log("Checking if token exists in localStorage...");
    console.log("hi");
    
    if (token) {
      console.log("Token found, verifying...");
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/verifyToken`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        console.log("Verification response:", data);
        if (res.status === 200 || data.success) {
          console.log("✅ Token valid, user logged in automatically");
          setIsLoggedIn(true);
          setUser(res.data.user);   
          navigate("/overview");
        } else {
          console.warn("⚠️ Invalid or expired token");
          localStorage.removeItem("jwtToken"); // Fixed key
        }
      } catch (error) {
        console.error("❌ Error verifying token:", error);
      }
    } else {
      console.log("No token found, skipping auto login");
    }
  };
  autoLogin();
}, [navigate]);

    useEffect(() => {
      const handleScroll = () => {
        const doc = document.documentElement;
        const scrollTop = window.scrollY || doc.scrollTop;
        const viewportHeight = window.innerHeight;
        const fullHeight = doc.scrollHeight;
        const scrollProgress = (scrollTop + viewportHeight) / Math.max(fullHeight, 1);

        if (scrollProgress >= 0.5) {
          setShowRecruiterModal(true);
          window.removeEventListener("scroll", handleScroll, { passive: true });
        }
      };
      window.addEventListener("scroll", handleScroll, { passive: true });
      // Run once in case the page is short or already scrolled
      handleScroll();
      return () => {
        window.removeEventListener("scroll", handleScroll, { passive: true });
      };
    }, []);
    const problems = [
      { icon: Users, text: "Connections to Recruiters", bg: "bg-red-50", textColor: "text-red-500", status: "coming-soon" },
      { icon: Flame, text: "Getting Interview Calls", bg: "bg-purple-50", textColor: "text-purple-500", status: "coming-soon" },
      { icon: Clock, text: "Automated Application Process", bg: "bg-blue-50", textColor: "text-blue-500", status: "live" },
      { icon: Inbox, text: "Job Application Tracking", bg: "bg-green-50", textColor: "text-green-500", status: "coming-soon" },
      { icon: Wand2, text: "Faster Referrals", bg: "bg-violet-50", textColor: "text-violet-500", status: "live" },
      { icon: Bell, text: "Quick Job Opening Updates", bg: "bg-yellow-50", textColor: "text-yellow-600", status: "coming-soon" },
    ];

    const trustPoints = [
      { icon: Target, label: "Apply smartly, not randomly" },
      { icon: Bell, label: "Every application acknowledged" },
      { icon: Activity, label: "Live status, end to end" },
      { icon: ShieldCheck, label: "Nothing falls through the cracks" },
    ];

    const platforms = [
      { name: "LinkedIn", color: "bg-[#0A66C2]", url: "https://www.linkedin.com/jobs/" },
      { name: "Naukri.com", color: "bg-[#4A90D9]", url: "https://www.naukri.com/" },
      { name: "Indeed", color: "bg-[#2164F3]", url: "https://www.indeed.com/" },
      { name: "Cutshort", color: "bg-[#5B47FB]", url: "https://cutshort.io/" },
      { name: "Hirist", color: "bg-[#FF6B35]", url: "https://www.hirist.com/" },
      { name: "Instahyre", color: "bg-[#F5574F]", url: "https://www.instahyre.com/" },
      { name: "Wellfound", color: "bg-black", url: "https://wellfound.com/" },
      { name: "Foundit", color: "bg-[#E8491D]", url: "https://www.foundit.in/" },
      { name: "Internshala", color: "bg-[#0C8B51]", url: "https://internshala.com/" },
      { name: "Glassdoor", color: "bg-[#0CAA41]", url: "https://www.glassdoor.com/" },
    ];

    return (
      <div className="bg-white text-gray-900">
        {/* ================= HERO ================= */}
        <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-indigo-50/60 to-white">
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="pointer-events-none absolute top-1/3 -left-24 h-72 w-72 rounded-full bg-indigo-200/40 blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
            {/* Left: Copy */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center lg:text-left"
            >
              <span className="inline-flex items-center gap-2 bg-white border border-blue-100 shadow-sm text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                Built so no application ever gets lost
              </span>

              <h1 className="text-4xl md:text-5xl lg:text-[3.3rem] font-bold leading-tight tracking-tight mb-6">
                Apply smart.{" "}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Never lose an application again.
                </span>
              </h1>

              <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
                RealHired helps you prepare and apply the smart way, then tracks every
                single application from Applied to Interview to Offer. Every application
                is acknowledged — so you always know exactly what happened to it.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mb-10">
                <button
                  onClick={() => navigate("/pricing")}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-xl font-semibold shadow-lg shadow-blue-600/20 hover:bg-blue-700 hover:shadow-blue-600/30 transition-all"
                >
                  Get Started for Free
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => navigate("/application-status")}
                  className="inline-flex items-center gap-2 bg-white text-gray-800 px-7 py-3.5 rounded-xl font-semibold border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
                >
                  Track My Applications
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl mx-auto lg:mx-0">
                {trustPoints.map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white border border-gray-100 shadow-sm shrink-0">
                      <Icon className="w-4 h-4 text-blue-600" />
                    </span>
                    <span className="text-left">{label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: Product preview card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative mx-auto w-full max-w-md"
            >
              <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                  </div>
                  <span className="text-xs font-medium text-gray-400">Application Tracker</span>
                </div>

                <div className="space-y-3">
                  {[
                    { label: "Applied", value: "Senior Frontend Engineer", color: "bg-blue-500" },
                    { label: "Interview", value: "Product Analyst", color: "bg-indigo-500" },
                    { label: "Offer", value: "Backend Developer", color: "bg-green-500" },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <span className={`h-2.5 w-2.5 rounded-full ${row.color}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{row.value}</p>
                        <p className="text-xs text-gray-500">{row.label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex items-center justify-between rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-white">
                  <div>
                    <p className="text-xs text-blue-100">Applications acknowledged</p>
                    <p className="text-xl font-bold">3 / 3</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-blue-100" />
                </div>
              </div>

              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl border border-gray-100 px-4 py-3 flex items-center gap-2"
              >
                <Bell className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-semibold text-gray-700">Nothing left behind</span>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ================= UNIFIED PLATFORMS ================= */}
        <section className="py-20 bg-white border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-4"
            >
              <Layers className="w-3.5 h-3.5" />
              One place for every job board
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="text-3xl md:text-4xl font-bold mb-3 text-gray-900"
            >
              Stop juggling fifteen tabs
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-gray-600 max-w-2xl mx-auto mb-12"
            >
              LinkedIn, Naukri, Cutshort, Hirist — you're already checking all of them separately.
              RealHired unifies openings from every platform job-seekers actually use, so you
              search once and track everything in one dashboard.
            </motion.p>

            <div className="flex flex-wrap items-center justify-center gap-3 max-w-4xl mx-auto">
              {platforms.map((platform, index) => (
                <motion.a
                  key={platform.name}
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.35, delay: (index % 10) * 0.05 }}
                  whileHover={{ y: -3 }}
                  className="group flex items-center gap-2.5 bg-white border border-gray-200 rounded-xl pl-3 pr-4 py-2.5 shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                >
                  <span
                    className={`flex items-center justify-center w-7 h-7 rounded-lg ${platform.color} text-white text-xs font-bold shrink-0`}
                  >
                    {platform.name.charAt(0)}
                  </span>
                  <span className="text-sm font-semibold text-gray-800">{platform.name}</span>
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.35, delay: platforms.length * 0.05 }}
                className="flex items-center gap-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl pl-3 pr-4 py-2.5 shadow-sm"
              >
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/20 shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </span>
                <span className="text-sm font-semibold text-white">+ many more</span>
              </motion.div>
            </div>
          </div>
        </section>

        {loadingJobs ? (
  <div className="flex items-center justify-center py-14">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-gray-600 text-sm font-medium">
        Loading opportunities...
      </p>
    </div>
  </div>
) : jobs.length === 0 ? (
  <div className="max-w-7xl mx-auto px-6">
    <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200">
      <BriefcaseIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        No Jobs Available
      </h3>
      <p className="text-gray-600 text-sm">
        Check back soon for new opportunities
      </p>
    </div>
  </div>
) : (
  <section className="max-w-7xl mx-auto px-6 py-16">
    {/* ===== Heading ===== */}
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.4 }}
      className="flex items-end justify-between mb-8"
    >
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
          Latest Jobs
        </h2>
        <p className="text-gray-500 text-sm mt-1">
          Freshly posted roles, matched to what you're looking for
        </p>
      </div>
      <button
        onClick={() => navigate("/jobs")}
        className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700"
      >
        View all jobs
        <ArrowRight size={16} />
      </button>
    </motion.div>

    {/* ===== Job Cards Grid ===== */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {jobs.map((job, index) => (
        <motion.div
          key={job._id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.4, delay: (index % 3) * 0.08 }}
          className="group relative bg-white border border-gray-200 rounded-2xl p-4 hover:shadow-xl hover:border-blue-200 transition-all cursor-pointer hover:-translate-y-1"
        >
          {/* Gradient Accent */}
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity" />

          <JobCard job={job} viewMode="grid" />

          <div className="mt-3 pt-2 border-t border-gray-100 flex justify-end">
            <SaveButton
              jobId={job._id}
              onSaveSuccess={() =>
                console.log(`Job ${job._id} saved!`)
              }
            />
          </div>
        </motion.div>
      ))}
    </div>

    <button
      onClick={() => navigate("/jobs")}
      className="sm:hidden mt-6 w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-blue-600 border border-blue-200 rounded-xl py-2.5"
    >
      View all jobs
      <ArrowRight size={16} />
    </button>
  </section>
)}

<BlogCarousel />

        {/* Problem Cards Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-4"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Our Tools
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="text-3xl md:text-4xl font-bold mb-3 text-gray-900"
            >
              Everything that slows a job search down — solved
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-gray-600 max-w-2xl mx-auto mb-12"
            >
              Each tool below targets a real friction point in job hunting. Live tools are
              ready to use today; the rest are shipping soon.
            </motion.p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {problems.map((item, index) => {
                const Icon = item.icon;
                const isLive = item.status === "live";
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.4, delay: (index % 3) * 0.08 }}
                    whileHover={isLive ? { y: -4 } : undefined}
                    className={`group text-left rounded-2xl border p-6 bg-white transition-shadow ${
                      isLive
                        ? "cursor-pointer border-gray-200 hover:border-blue-200 hover:shadow-xl"
                        : "cursor-not-allowed border-gray-100 opacity-80"
                    }`}
                    onClick={() => {
                      if (isLive) {
                        if (item.text === "Automated Application Process") {
                          navigate("/email-sender");
                        } else if (item.text === "Faster Referrals") {
                          navigate("/jobs");
                        }
                      }
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${item.bg}`}>
                        <Icon className={`w-6 h-6 ${item.textColor}`} />
                      </div>

                      {isLive ? (
                        <span className="inline-flex items-center gap-1 bg-green-50 text-green-600 text-xs font-semibold px-2.5 py-1 rounded-full">
                          <CheckCircle2 className="w-3 h-3" />
                          Live
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-600 text-xs font-semibold px-2.5 py-1 rounded-full">
                          <Clock className="w-3 h-3" />
                          Coming Soon
                        </span>
                      )}
                    </div>

                    <p className="text-base font-semibold text-gray-900 mb-1">{item.text}</p>
                    {isLive && (
                      <p className="text-sm text-blue-600 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        Try it now
                        <ArrowRight size={14} />
                      </p>
                    )}
                  </motion.div>
                );
              })}
            </div>

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/register")}
              className="mt-12 inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-xl font-semibold shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-colors"
            >
              Sign up now, it's free
              <ArrowRight size={18} />
            </motion.button>
          </div>
        </section>

        {/* Mentors */}
        <MentorCarousel></MentorCarousel>

        {/* Chrome Extension */}
        <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
  <div className="max-w-7xl mx-auto px-6">

    <div className="grid md:grid-cols-2 gap-12 items-center">

      {/* LEFT CONTENT */}
      <div>
        <span className="inline-flex items-center gap-2 bg-white border border-blue-100 shadow-sm text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
          <Sparkles className="w-3.5 h-3.5" />
          Free Chrome Extension
        </span>

        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Apply to jobs faster with the RealHired extension
        </h2>

        <p className="text-gray-600 mb-6">
          Install the RealHired Chrome Extension and apply to jobs in seconds.
          Track applications, automate submissions, and never miss opportunities.
        </p>

        <ul className="space-y-3 mb-8 text-gray-700">
          {[
            "Auto-fill job applications",
            "Save jobs instantly from any website",
            "Track all your applications in one place",
            "Apply to jobs in one click",
          ].map((text) => (
            <li key={text} className="flex items-center gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
              </span>
              {text}
            </li>
          ))}
        </ul>

        <a
          href="https://chromewebstore.google.com/detail/realhired/kgoeiikjpklcekojlpjbleojjddolmnh"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-black text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-800 transition shadow-lg shadow-black/10"
        >
          <img
            src="https://www.google.com/chrome/static/images/chrome-logo.svg"
            className="w-5 h-5"
            alt="chrome"
          />
          Add to Chrome
          <ArrowRight size={18} />
        </a>

        <p className="text-xs text-gray-500 mt-3">
          Free forever. No credit card required.
        </p>
      </div>

      {/* RIGHT VISUAL */}
      <div className="relative">

        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">

          <div className="flex items-center gap-3 mb-4">
            <img
              src="https://www.google.com/chrome/static/images/chrome-logo.svg"
              className="w-8"
              alt=""
            />
            <div>
              <p className="font-semibold">RealHired Extension</p>
              <p className="text-sm text-gray-500">Apply to jobs faster</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded w-4/5"></div>
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-3/5"></div>
          </div>

          <button className="mt-5 w-full bg-blue-600 text-white py-2.5 rounded-xl font-medium">
            Apply with RealHired
          </button>

        </div>

      </div>
    </div>
  </div>
</section>
        <HelpSection></HelpSection>
        <FAQSection></FAQSection>
        <PricingPage />
        <RecruiterPromptModal
          open={showRecruiterModal}
          onClose={() => setShowRecruiterModal(false)}
          onCta={() => {
            setShowRecruiterModal(false);
            navigate("/recruiters");
          }}
        />
      </div>
    );
  };
  export default HomePage;
  
